import { Component, Inject, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ContextoBackend } from '@app/models/auth/contexto-backend';
import { AuthService } from '@app/services/auth/auth.service';
import { ServicioSesion } from '@app/services/sesion/sesion.service';

export interface SelectRolDialogData {
  contextos: ContextoBackend[];
}

@Component({
  selector: 'app-select-rol',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatSelectModule,
    MatFormFieldModule,
    MatIconModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './select-rol.component.html',
  styleUrls: ['./select-rol.component.scss'],
})
export class SelectRolDialogComponent {
  private _authService = inject(AuthService);
  private _sesion = inject(ServicioSesion);

  public contextoControl = new FormControl<ContextoBackend | null>(null, Validators.required);
  public filterControl = new FormControl('');
  public cargando = signal(false);

  public globales: ContextoBackend[] = [];
  public equipos: { nombre: string; contextos: ContextoBackend[] }[] = [];
  public ligas: { nombre: string; contextos: ContextoBackend[] }[] = [];
  public escuelas: { nombre: string; contextos: ContextoBackend[] }[] = [];

  public globalesFiltrados: ContextoBackend[] = [];
  public equiposFiltrados: { nombre: string; contextos: ContextoBackend[] }[] = [];
  public ligasFiltradas: { nombre: string; contextos: ContextoBackend[] }[] = [];
  public escuelasFiltradas: { nombre: string; contextos: ContextoBackend[] }[] = [];

  constructor(
    public dialogRef: MatDialogRef<SelectRolDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SelectRolDialogData
  ) {
    this.procesarContextos(data.contextos);
    this.filterControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(texto => this.filtrarContextos(texto ?? ''));
  }

  private procesarContextos(contextos: ContextoBackend[]): void {
    this.globales = contextos.filter(c => c.tipo === 'global');

    const equipoMap = new Map<string, { nombre: string; contextos: ContextoBackend[] }>();
    contextos
      .filter(c => c.tipo === 'equipo')
      .forEach(c => {
        const key = c.team_id!;
        if (!equipoMap.has(key)) {
          equipoMap.set(key, { nombre: c.equipo_nombre ?? `Equipo ${key}`, contextos: [] });
        }
        equipoMap.get(key)!.contextos.push(c);
      });

    this.equipos = Array.from(equipoMap.values())
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }));

    const ligaMap = new Map<string, { nombre: string; contextos: ContextoBackend[] }>();
    contextos
      .filter(c => c.tipo === 'liga')
      .forEach(c => {
        const key = c.liga_id!;
        if (!ligaMap.has(key)) {
          ligaMap.set(key, { nombre: c.liga_nombre ?? `Liga ${key}`, contextos: [] });
        }
        ligaMap.get(key)!.contextos.push(c);
      });

    this.ligas = Array.from(ligaMap.values())
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }));

    const escuelaMap = new Map<string, { nombre: string; contextos: ContextoBackend[] }>();
    contextos
      .filter(c => c.tipo === 'escuela')
      .forEach(c => {
        const key = c.team_id!;
        if (!escuelaMap.has(key)) {
          escuelaMap.set(key, { nombre: c.escuela_nombre ?? `Escuela ${key}`, contextos: [] });
        }
        escuelaMap.get(key)!.contextos.push(c);
      });

    this.escuelas = Array.from(escuelaMap.values())
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }));

    this.filterControl.setValue('');
    this.filtrarContextos('');
  }

  private filtrarContextos(texto: string): void {
    const busqueda = texto.trim().toLowerCase();

    if (!busqueda) {
      this.globalesFiltrados = this.globales;
      this.ligasFiltradas = this.ligas;
      this.equiposFiltrados = this.equipos;
      this.escuelasFiltradas = this.escuelas;
      return;
    }

    this.globalesFiltrados = this.globales.filter(c => this.etiqueta(c).toLowerCase().includes(busqueda));

    this.equiposFiltrados = this.equipos
      .map(equipo => ({
        nombre: equipo.nombre,
        contextos: equipo.nombre.toLowerCase().includes(busqueda)
          ? equipo.contextos
          : equipo.contextos.filter(c => c.rol.toLowerCase().includes(busqueda)),
      }))
      .filter(equipo => equipo.contextos.length > 0);

    this.ligasFiltradas = this.ligas
      .map(liga => ({
        nombre: liga.nombre,
        contextos: liga.nombre.toLowerCase().includes(busqueda)
          ? liga.contextos
          : liga.contextos.filter(c => c.rol.toLowerCase().includes(busqueda)),
      }))
      .filter(liga => liga.contextos.length > 0);

    this.escuelasFiltradas = this.escuelas
      .map(escuela => ({
        nombre: escuela.nombre,
        contextos: escuela.nombre.toLowerCase().includes(busqueda)
          ? escuela.contextos
          : escuela.contextos.filter(c => c.rol.toLowerCase().includes(busqueda)),
      }))
      .filter(escuela => escuela.contextos.length > 0);
  }

  public actualizarContextos(): void {
    this.cargando.set(true);
    this._authService.getContextos().subscribe({
      next: (res) => {
        this._sesion.guardarContextos(res.contextos);
        this.contextoControl.reset();
        this.procesarContextos(res.contextos);
        this.cargando.set(false);
      },
      error: () => this.cargando.set(false),
    });
  }

  public etiqueta(contexto: ContextoBackend): string {
    return contexto.tipo === 'global'
      ? `${contexto.rol} (Global)`
      : contexto.rol;
  }

  public confirmarSeleccion(): void {
    if (this.contextoControl.valid && this.contextoControl.value) {
      this.dialogRef.close(this.contextoControl.value);
    }
  }
}
